import {
  Box,
  Breakpoint,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import Form from 'components/Form';
import { getCollegeList } from 'apis/admin';
import { postRecoverEmail } from 'apis/account';
import useNotification from 'hooks/useNotification';
import MuiAutocomplete from 'routes/SignUp/components/MuiAutocomplete';

interface Props {
  title: string;
  isOpen?: boolean;
  maxWidth?: Breakpoint;
  onClose: () => void;
  onSwitchToPasswordFind?: () => void;
}

interface ICollegeOption {
  college_id: number;
  college_name: string;
}

type Step = 'input' | 'success' | 'notfound';

function Confirm(props: Props) {
  const {
    isOpen = false,
    maxWidth = 'xs',
    onClose,
    onSwitchToPasswordFind,
  } = props;
  const navigate = useNavigate();
  const { onFail } = useNotification();
  const { palette } = useTheme();

  const [step, setStep] = useState<Step>('input');
  const [userName, setUserName] = useState('');
  const [college, setCollege] = useState<ICollegeOption | null>(null);
  const [studentNo, setStudentNo] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isSubmitEnabled = useMemo(
    () =>
      userName.trim().length > 0 &&
      Boolean(college?.college_id) &&
      studentNo.trim().length > 0,
    [userName, college, studentNo]
  );

  const resetAll = () => {
    setStep('input');
    setUserName('');
    setCollege(null);
    setStudentNo('');
    setMaskedEmail('');
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  const handleStudentNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');
    setStudentNo(onlyNumbers);
  };

  const handleSubmit = async () => {
    if (!isSubmitEnabled || isLoading) return;

    setIsLoading(true);
    try {
      const { data } = await postRecoverEmail({
        student_name: userName.trim(),
        student_no: studentNo.trim(),
        student_college: college!.college_id,
      });

      if (data?.rc === 1 && data?.masked_email) {
        setMaskedEmail(data.masked_email);
        setStep('success');
      } else {
        setStep('notfound');
      }
    } catch (e) {
      onFail('Failed to find your email.', e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoToLogin = () => {
    handleClose();
  };

  const handleGoToPasswordFind = () => {
    resetAll();
    onClose();
    onSwitchToPasswordFind?.();
  };

  const handleRetry = () => {
    setStep('input');
  };

  const handleChannelTalk = () => {
    window.open('https://vd48q.channel.io/');
  };

  const handleGoToSignUp = () => {
    handleClose();
    navigate('/signup');
  };

  const renderInputStep = () => (
    <>
      <Box
        sx={{
          backgroundColor: palette.primary.light,
          borderRadius: 1.5,
          p: 2,
          mb: 3,
        }}
      >
        <Typography sx={{ fontSize: 14, color: palette.text.primary, mb: 0.5 }}>
          Did you only sign up on the website?
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            color: palette.text.primary,
            mb: 1.5,
            lineHeight: 1.6,
          }}
        >
          For your privacy, a separate ENR registration is required.
          <br />
          If you do not have an ENR account, we cannot find your email.
        </Typography>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          onClick={handleGoToSignUp}
          sx={{
            backgroundColor: 'transparent',
            fontWeight: 700,
            '&:hover': { backgroundColor: 'transparent' },
          }}
        >
          Smart ENR Sign up
        </Button>
      </Box>

      <Stack spacing={2}>
        <Form.Item label="Name">
          <TextField
            fullWidth
            size="small"
            placeholder="Please enter your name."
            value={userName}
            onChange={e => setUserName(e.target.value)}
            autoComplete="off"
          />
        </Form.Item>

        <Form.Item label="School">
          <MuiAutocomplete
            listKey="college_lists"
            valueKey="college_id"
            size="small"
            hideSearchAdornment
            noOptionsText="Please enter another school name"
            placeholder="Enter or select your school name."
            value={college}
            onChange={(value: ICollegeOption | null) => setCollege(value)}
            getApi={getCollegeList}
            getOptionLabel={(option: ICollegeOption) => option.college_name}
          />
        </Form.Item>

        <Form.Item label="Student ID">
          <TextField
            fullWidth
            size="small"
            placeholder="Please enter your student ID."
            value={studentNo}
            onChange={handleStudentNoChange}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            autoComplete="off"
          />
        </Form.Item>

        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          loading={isLoading}
          disabled={!isSubmitEnabled}
          onClick={handleSubmit}
          sx={{ mt: 1, mb: 2 }}
        >
          Find Email
        </LoadingButton>
      </Stack>
    </>
  );

  const renderSuccessStep = () => (
    <>
      <Box
        sx={{
          border: '1px solid #D1D5DB',
          borderRadius: 1.5,
          py: 3.5,
          px: 2,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 500, color: palette.text.primary }}>
          {maskedEmail}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <Button
          fullWidth
          size="large"
          variant="outlined"
          onClick={handleGoToPasswordFind}
        >
          Forgot password
        </Button>
        <Button
          fullWidth
          size="large"
          variant="contained"
          onClick={handleGoToLogin}
        >
          Log in
        </Button>
      </Stack>
    </>
  );

  const renderNotFoundStep = () => (
    <>
      <Box
        sx={{
          backgroundColor: '#FDECEC',
          borderRadius: 1.5,
          py: 3.5,
          px: 2,
          mb: 3,
          textAlign: 'center',
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            backgroundColor: '#F8C9C9',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 1.5,
          }}
        >
          <ErrorOutlineIcon sx={{ color: '#D9322B', fontSize: 32 }} />
        </Box>
        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: palette.text.primary,
            mb: 1,
          }}
        >
          No matching account found
        </Typography>
        <Typography sx={{ fontSize: 14, color: '#4B5563', lineHeight: 1.6 }}>
          There is no registered email
          <br />
          matching the information you entered.
        </Typography>
      </Box>

      <Stack spacing={1.5} sx={{ mb: 1 }}>
        <Button
          fullWidth
          size="large"
          variant="contained"
          onClick={handleRetry}
        >
          Try again
        </Button>
        <Button
          fullWidth
          size="large"
          variant="outlined"
          onClick={handleChannelTalk}
        >
          Contact Us
        </Button>
      </Stack>

      <Box sx={{ textAlign: 'center', mt: 1.5, mb: 1 }}>
        <Link
          component="button"
          type="button"
          underline="always"
          onClick={handleGoToSignUp}
          sx={{ fontSize: 13, color: '#4B5563' }}
        >
          Please sign up for ENR first.
        </Link>
      </Box>
    </>
  );

  const dialogTitle =
    step === 'success' ? 'Registered Email' : 'Forgot your Email?';

  return (
    <Dialog
      maxWidth={maxWidth}
      fullWidth
      open={isOpen}
      onClose={handleClose}
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 0 }}>
        <DialogActions sx={{ p: 0 }}>
          <Button
            variant="text"
            onClick={handleClose}
            sx={{ minWidth: 0, marginRight: '-20px', color: 'black' }}
          >
            <CloseIcon />
          </Button>
        </DialogActions>
        <Typography sx={{ fontSize: '24px', fontWeight: 700 }}>
          {dialogTitle}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {step === 'input' && renderInputStep()}
        {step === 'success' && renderSuccessStep()}
        {step === 'notfound' && renderNotFoundStep()}
      </DialogContent>
    </Dialog>
  );
}

export default Confirm;
